import { EventBody, Question, Option } from '../../../gds-api-client';
import { ActionItem, Field } from '../../../gs-component-form';
import { InlineAlert } from '@xo-union/tk-component-alerts';
import { FormColumn, FormRow } from '@xo-union/tk-component-form-grid';
import { IconButton } from '@xo-union/tk-component-icons';
import { Field as FormikField, FieldArray, FieldArrayRenderProps, FormikProps } from 'formik';
import React, { useContext } from 'react';
import get from 'lodash/get';

import { FormValues, InitialValues } from '../../LegacyModal/RightRailForm';
import { ApiClientsContext } from '../ApiClientsContext';
import { ErrorContext } from '../ErrorContext';
import RightRailSection from '../RightRailSection';
import styles from './meals.scss';
import { emptyMealQuestion } from '../../ScheduleOfEvents/helpers';
import addMealQuestionToQuestions from '../../helpers/addMealQuestionToQuestions';

interface ArrayHelpers extends FieldArrayRenderProps {
  form: FormikProps<FormValues | EventBody>;
}

interface Props {
  namePrefix?: string;
}

interface EventBodyWithTimeStamps extends EventBody {
  createdAt?: string;
  updatedAt?: string;
}

interface MealFunction {
  arrayHelpers: ArrayHelpers;
  updatedMeal: Option;
  index: number;
}

const updateFormikMealOptions = (options: Option[], arrayHelpers: ArrayHelpers): void => {
  arrayHelpers.remove(options.length);
  options.forEach((option, idx) => {
    arrayHelpers.replace(idx, option);
  });
};

export const Meals: React.FC<Props> = ({ namePrefix = '' }) => {
  const { errors, setErrors, clearError } = useContext(ErrorContext);
  const { gdsEvent, setGdsEvent } = useContext(ApiClientsContext);

  let event: any;
  let eventId: any;
  let mealQuestion: any;
  let eventMeals: any;
  if (gdsEvent) {
    event = Array.isArray(gdsEvent) ? gdsEvent[0] : gdsEvent;
    eventId = event.id;
    const { questions } = event;
    if (questions && questions.length) {
      mealQuestion = questions.find((question: any) => question.type === 'Meal');
      eventMeals = get(mealQuestion, ['options']) || [];
    } else {
      eventMeals = [];
    }
  }

  const removeMeal = async ({ arrayHelpers, updatedMeal, index }: MealFunction): Promise<void> => {
    clearError();
    try {
      if (event) {
        if (event.questions) {
          let filteredOptions: any;
          event.questions = event.questions.map((question: any) => {
            if (question.options && question.type === 'Meal') {
              const newQuestion = {
                ...question,
                options: question.options.filter((_: any, idx: any) => idx !== index),
              };
              filteredOptions = newQuestion.options;
              return newQuestion;
            }

            return question;
          });

          if (eventId && updatedMeal.text) {
            // update local state when meal is deleted
            // @ts-ignore
            updateFormikMealOptions(filteredOptions, arrayHelpers);
            // @ts-ignore
            setGdsEvent(event);
          } else {
            arrayHelpers.remove(index);
          }
        } else {
          arrayHelpers.remove(index);
        }
      }
      // @ts-ignore
    } catch ({ response }) {
      setErrors({ meals: 'Oops! Something went wrong. Please try again later.' });
    }
  };

  const updateMeal = async ({ arrayHelpers, updatedMeal, index }: MealFunction): Promise<void> => {
    clearError();
    if (!updatedMeal || !updatedMeal.text) return;
    try {
      if (eventId && eventMeals) {
        const isChangeOnExistingMeal = eventMeals[index];
        mealQuestion = mealQuestion || emptyMealQuestion();

        if (isChangeOnExistingMeal) {
          mealQuestion.options[index] = updatedMeal;
        } else if (mealQuestion.options && mealQuestion.options.length) {
          mealQuestion.options = [...mealQuestion.options, updatedMeal];
        } else {
          mealQuestion.options = [updatedMeal];
        }

        event.questions = addMealQuestionToQuestions(event.questions, mealQuestion);

        mealQuestion.options.forEach((option: any, idx: any) => {
          arrayHelpers.replace(idx, option);
        });
        setGdsEvent(event);
      }
      // @ts-ignore
    } catch ({ response }) {
      setErrors({ meals: 'Oops! Something went wrong. Please try again later.' });
    }
  };

  return (
    <RightRailSection name="Event Meal Options">
      {errors && errors.meals && <InlineAlert type="error">{errors.meals}</InlineAlert>}
      <FieldArray name={`${namePrefix}mealQuestion.options`}>
        {(arrayHelpers: ArrayHelpers): JSX.Element => {
          const formMealQuestion: Question =
            (arrayHelpers.form.values as InitialValues).mealQuestion ||
            (arrayHelpers.form.values as FormValues).events[0].mealQuestion ||
            emptyMealQuestion();
          const mealOptions = formMealQuestion.options || [];

          return (
            <>
              {mealOptions.map((meal, index) => (
                // eslint-disable-next-line
                <FormRow key={index}>
                  <FormColumn xs="8" sm md>
                    <FormikField
                      focus={(index === mealOptions.length - 1).toString()}
                      name={`${namePrefix}mealQuestion.options.${index}.text`}
                      label="Meal Name"
                      onBlur={(): Promise<void> => updateMeal({ arrayHelpers, updatedMeal: mealOptions[index], index })}
                      component={Field}
                    />
                  </FormColumn>
                  <FormColumn style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <IconButton
                      aria-label="Remove meal"
                      data-testid={`remove-meal-${index}`}
                      className={styles.removeItem}
                      size="md"
                      name="delete"
                      // @ts-ignore
                      onClick={(): Promise<void> => removeMeal({ arrayHelpers, updatedMeal: meal, index })}
                      type="button"
                    />
                  </FormColumn>
                </FormRow>
              ))}
              <ActionItem
                type="add"
                onClick={(): void =>
                  arrayHelpers.push({
                    text: '',
                  })
                }
              >
                Add New Meal Option
              </ActionItem>
            </>
          );
        }}
      </FieldArray>
    </RightRailSection>
  );
};
